import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Checkbox } from '../components/ui/checkbox';
import { CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SetupGuides = () => {
  const { t, language } = useLanguage();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGuides();
  }, []);

  const fetchGuides = async () => {
    try {
      const { data } = await axios.get(`${API}/setup-guides`, { withCredentials: true });
      setGuides(data);
    } catch (error) {
      console.error('Failed to fetch guides:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleComplete = async (guideId, currentStatus) => {
    try {
      await axios.put(
        `${API}/setup-guides/${guideId}/complete`,
        null,
        { params: { completed: !currentStatus }, withCredentials: true }
      );
      setGuides(guides.map(g => g.id === guideId ? {...g, completed: !currentStatus} : g));
    } catch (error) {
      console.error('Failed to update guide:', error);
    }
  };

  const sections = [...new Set(guides.map(g => g.section))];
  const getSectionTitle = (section) => {
    if (section === 'channel_creation') return t('Channel Creation', 'إنشاء القناة');
    if (section === 'optimization') return t('Channel Optimization', 'تحسين القناة');
    return section;
  };

  const completedCount = guides.filter(g => g.completed).length;
  const totalCount = guides.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-3xl font-black tracking-tight text-[#EDEDED]">
          {t('YouTube Setup Guides', 'أدلة إعداد يوتيوب')}
        </h2>
        <p className="text-[#A1A1AA] mt-1">
          {t('Complete these steps to optimize your channel', 'أكمل هذه الخطوات لتحسين قناتك')}
        </p>
      </div>

      {/* Progress Bar */}
      <div className="bg-carbon border border-[#27272A] rounded-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[#EDEDED] font-semibold">{t('Progress', 'التقدم')}</span>
          <span className="text-mecha-blue font-bold">{completedCount}/{totalCount}</span>
        </div>
        <div className="h-3 bg-obsidian rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-safety-amber to-mecha-blue transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center text-[#A1A1AA] py-12">{t('Loading...', 'تحميل...')}</div>
      ) : (
        <div className="space-y-6">
          {sections.map(section => (
            <div key={section} className="bg-carbon border border-[#27272A] rounded-lg p-6">
              <h3 className="font-heading text-xl font-bold text-safety-amber mb-4">
                {getSectionTitle(section)}
              </h3>
              <Accordion type="single" collapsible className="space-y-2">
                {guides
                  .filter(g => g.section === section)
                  .map(guide => (
                    <AccordionItem
                      key={guide.id}
                      value={guide.id}
                      className="border border-[#27272A] rounded-lg overflow-hidden"
                      data-testid={`guide-${guide.id}`}
                    >
                      <AccordionTrigger className="px-4 py-3 hover:bg-white/5 hover:no-underline">
                        <div className="flex items-center gap-3 flex-1 text-start">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleComplete(guide.id, guide.completed);
                            }}
                            className="cursor-pointer"
                          >
                            {guide.completed ? (
                              <CheckCircle2 className="w-5 h-5 text-mecha-blue" />
                            ) : (
                              <div className="w-5 h-5 border-2 border-[#52525B] rounded-full" />
                            )}
                          </div>
                          <span className={`font-semibold ${
                            guide.completed ? 'text-[#A1A1AA] line-through' : 'text-[#EDEDED]'
                          }`}>
                            {language === 'ar' ? guide.title_ar : guide.title_en}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 py-3 text-[#A1A1AA] bg-obsidian/50">
                        {language === 'ar' ? guide.description_ar : guide.description_en}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
              </Accordion>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SetupGuides;
